import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useCart } from '../context/CartContext';
import PaymentGrid from './PaymentLogos';
import { ConfiguratorShowcase } from './configurator/ConfiguratorShowcase';
import { ConfiguratorControls } from './configurator/ConfiguratorControls';
import { ConfiguratorTotal } from './configurator/ConfiguratorTotal';
import { StickyMiniCart } from './configurator/StickyMiniCart';
import MiniReviews from './MiniReviews';
import { calculateLocal } from '../lib/calculator';
import { trackEvent, installFunnelDropOffTracker, debounce } from '../lib/analytics';

/**
 * E-Commerce Card-Configurator v6 — helles Editorial-Design.
 * Links: Card im dunklen Showcase + Vorteile · Rechts: Konfiguration + Total.
 */
export default function CardConfigurator({
  initialMode = 'private',
  lockMode = false,
  compact = false,
  showHeading = true,
  hideAvatar = false,
}) {
  const [mode, setMode] = useState(initialMode);
  const [vehicles, setVehicles] = useState(initialMode === 'business' ? 5 : 1);
  const [annualKm, setAnnualKm] = useState(15000);
  const [employees, setEmployees] = useState(50);
  const [householdAdults, setHouseholdAdults] = useState(1);
  const [householdVehicles, setHouseholdVehicles] = useState(1);
  const { openCart, setConfig: setCartConfig } = useCart();

  // Sticky-Mini-Cart: erscheint sobald User über das Hero-Total hinaus gescrollt ist
  const heroTotalRef = useRef(null);
  const [showSticky, setShowSticky] = useState(false);
  const [stickyDismissed, setStickyDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage?.getItem('fuelx_sticky_cart_dismissed_v1') === '1';
  });

  useEffect(() => {
    if (stickyDismissed) {
      setShowSticky(false);
      return undefined;
    }
    const onScroll = () => {
      const el = heroTotalRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setShowSticky(rect.bottom < 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [stickyDismissed]);

  const dismissSticky = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage?.setItem('fuelx_sticky_cart_dismissed_v1', '1');
    }
    setStickyDismissed(true);
    setShowSticky(false);
  };

  const isPvt = mode === 'private';
  const cardName = isPvt ? 'Silver Card' : 'Gold Card';
  const cardImgFront = isPvt ? '/silver-card-front-768.webp' : '/gold-card-front-768.webp';
  const cardImgBack = isPvt ? '/silver-card-back-768.webp' : '/gold-card-back-768.webp';

  // ── Live-Kalkulation im Frontend (kein API-Call, latenzfrei) ──
  const result = useMemo(
    () => calculateLocal({
      customer_type: mode,
      vehicles,
      annual_km: annualKm,
      employees: mode === 'business' ? employees : 0,
    }),
    [mode, vehicles, annualKm, employees],
  );
  const loading = false;

  useEffect(() => {
    if (mode === 'business' && employees < vehicles) setEmployees(vehicles);
  }, [vehicles, mode, employees]);

  // Übernahme aus dem Flotten-Rechner (sessionStorage bei Navigation, CustomEvent auf gleicher Seite)
  const applyPrefill = useCallback((p) => {
    if (!p || !p.vehicles) return;
    if (initialMode !== 'business' && !lockMode) setMode('business');
    if (initialMode !== 'business' && lockMode) return;
    const v = Math.max(1, Math.min(200, p.vehicles));
    setVehicles(v);
    setAnnualKm(Math.max(5000, Math.min(50000, p.annualKm || 25000)));
    setEmployees(Math.max(v, p.employees || v));
  }, [initialMode, lockMode]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('fuelx_fleet_prefill');
      if (raw) {
        applyPrefill(JSON.parse(raw));
        sessionStorage.removeItem('fuelx_fleet_prefill');
      }
    } catch (e) { /* noop */ }
    const onPrefill = (e) => applyPrefill(e.detail);
    window.addEventListener('fuelx-fleet-prefill', onPrefill);
    return () => window.removeEventListener('fuelx-fleet-prefill', onPrefill);
  }, [applyPrefill]);

  // ── Funnel-Tracking: Configurator-Entry + Drop-off-Detector installieren ──
  // useRef-Guard verhindert Doppel-Fire durch React 18 StrictMode
  const entryFired = useRef(false);
  useEffect(() => {
    if (entryFired.current) return;
    entryFired.current = true;
    trackEvent('configurator_entry', { mode: initialMode });
    installFunnelDropOffTracker();
  }, [initialMode]);

  // Debounced-Change-Events (Slider spammed sonst)
  const trackKmChange = useMemo(() => debounce((val, m) => {
    trackEvent('configurator_km_change', { km: val, mode: m });
  }, 500), []);
  const trackVehicleChange = useMemo(() => debounce((val, m) => {
    trackEvent('configurator_vehicles_change', { vehicles: val, mode: m });
  }, 500), []);

  useEffect(() => { trackKmChange(annualKm, mode); }, [annualKm, mode, trackKmChange]);
  useEffect(() => { trackVehicleChange(vehicles, mode); }, [vehicles, mode, trackVehicleChange]);

  const handleCheckout = () => {
    trackEvent('configurator_cta_click', {
      mode,
      vehicles,
      km: annualKm,
      card_fee: result?.card_fee,
      tank_credit: result?.tank_credit,
    });
    openCart({
      mode,
      vehicles,
      annualKm,
      employees,
      householdAdults,
      householdVehicles,
      cardFee: result?.card_fee,
      tankCredit: result?.tank_credit,
    });
  };

  const switchMode = (m) => {
    if (lockMode) return;
    trackEvent('configurator_mode_switch', { from: mode, to: m });
    setMode(m);
    if (m === 'private') { setVehicles(1); setAnnualKm(15000); }
    else { setVehicles(5); setAnnualKm(25000); setEmployees(50); }
  };

  const totalCredit = result?.tank_credit || 0;
  const hotelNightsMax = Math.ceil(totalCredit / 25);
  const totalAmount = Number(result?.card_fee) || 0;
  const fmt = (v, d = 2) => v.toLocaleString('de-DE', { minimumFractionDigits: d });

  // ── Konfiguration live in den globalen Warenkorb spiegeln (erst nach Interaktion) ──
  // Damit zeigen Sticky-Bar, Warenkorb-Drawer und Kasse dieselbe Auswahl & Summe.
  const configTouched = useRef(false);
  const markTouched = () => { configTouched.current = true; };
  useEffect(() => {
    if (!configTouched.current) return undefined;
    const handle = setTimeout(() => {
      setCartConfig({ mode, vehicles, annualKm, employees, householdAdults, householdVehicles });
    }, 250);
    return () => clearTimeout(handle);
  }, [mode, vehicles, annualKm, employees, householdAdults, householdVehicles, setCartConfig]);

  return (
    <section
      className={`${compact ? 'py-12' : 'py-14 sm:py-20'} bg-white`}
      data-testid="card-configurator"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {showHeading && (
          <div className="mb-12">
            <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-stone-500 mb-4">Konfigurator</p>
            <h2
              className="font-display text-stone-900 leading-[0.95] tracking-[-0.028em] text-4xl sm:text-5xl mb-4"
              data-testid="configurator-heading"
            >
              Deine FuelX Card.
            </h2>
            <p className="font-body-refined text-base text-stone-500 max-w-xl">
              Konfiguriere, sieh dein FuelGood Guthaben live — und bestelle direkt.
            </p>
          </div>
        )}

        <div className="bg-white rounded-[2rem] border border-stone-200 overflow-hidden neon-frame">
          <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_1fr]">
            <div className="min-w-0">
              <ConfiguratorShowcase
                cardName={cardName}
                cardImgFront={cardImgFront}
                cardImgBack={cardImgBack}
                business={!isPvt}
                hideAvatar={hideAvatar}
              />
            </div>

            {/* ═══ RIGHT — Konfiguration + Total ═══ */}
            <div className="p-4 sm:p-9 lg:p-10 flex flex-col min-w-0">
              <ConfiguratorControls
                mode={mode}
                lockMode={lockMode}
                isPvt={isPvt}
                vehicles={vehicles}
                annualKm={annualKm}
                employees={employees}
                householdAdults={householdAdults}
                householdVehicles={householdVehicles}
                onVehicles={(v) => { markTouched(); setVehicles(v); }}
                onAnnualKm={(v) => { markTouched(); setAnnualKm(v); }}
                onEmployees={(v) => { markTouched(); setEmployees(v); }}
                onSwitchMode={(m) => { markTouched(); switchMode(m); }}
                onHouseholdAdults={(v) => { markTouched(); setHouseholdAdults(v); }}
                onHouseholdVehicles={(v) => { markTouched(); setHouseholdVehicles(v); }}
              />

              <ConfiguratorTotal
                heroTotalRef={heroTotalRef}
                result={result}
                isPvt={isPvt}
                cardName={cardName}
                vehicles={vehicles}
                annualKm={annualKm}
                totalAmount={totalAmount}
                totalCredit={totalCredit}
                hotelNightsMax={hotelNightsMax}
                loading={loading}
                fmt={fmt}
                onCheckout={handleCheckout}
                hideAvatar={hideAvatar}
                mode={mode}
                householdVehicles={householdVehicles}
              />

              {/* Zahlungsmethoden */}
              <div className="mt-auto pt-5 border-t border-stone-100">
                <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-stone-500 mb-2.5 text-center">Zahlungsmethoden</p>
                <div className="max-w-sm mx-auto" data-testid="configurator-payment-preview">
                  <PaymentGrid cols={9} small />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ STICKY MINI-CART ═══ */}
      {showSticky && result && (
        <StickyMiniCart
          result={result}
          isPvt={isPvt}
          cardName={cardName}
          cardImgFront={cardImgFront}
          annualKm={annualKm}
          vehicles={vehicles}
          totalAmount={totalAmount}
          loading={loading}
          fmt={fmt}
          onCheckout={handleCheckout}
          onDismiss={dismissSticky}
        />
      )}

      {/* ═══ MOBILE: keine eigene Buy-Bar — die globale „Card sichern"-Leiste (StickyMobileCTA) übernimmt ═══ */}
    </section>
  );
}
